/*
 * tg-adult-evidence
 *
 * Citation lightboxes for the evidence section on pdp-ecom-adult-scam.
 *
 * Why this exists: every source reference in this section used to be an
 * outbound link. On the one section whose entire job is establishing
 * credibility, that meant six separate invitations to leave the page. These
 * modals show the citation in place, while still offering the outbound link
 * inside the panel for anyone who wants the original.
 *
 * Progressive enhancement: the triggers are real <button> elements and the
 * panels are plain markup with the `hidden` attribute. With JS disabled the
 * buttons simply do nothing and the panels stay hidden, which is why every
 * finding card can also fall back to a normal outbound link via source_index 0.
 *
 * Custom element rather than an IIFE so connectedCallback re-runs after a
 * Shopify theme-editor section reload. Guarded with dataset.ready so a
 * re-render cannot double-bind, matching assets/tg-adult-cta.js.
 *
 * Deliberately does NOT use .faq--item / .faq--question / .faq--answer.
 * sections/ecom-faq.liquid binds a document-scoped listener on those names and
 * they also drive the buy-box accordions in sections/ecom-product.liquid.
 */
if (!customElements.get("tg-adult-evidence")) {
  customElements.define(
    "tg-adult-evidence",
    class TgAdultEvidence extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        this.modals = Array.from(this.querySelectorAll("[data-tgae-modal]"));
        if (!this.modals.length) return;

        this.openers = Array.from(this.querySelectorAll("[data-tgae-open]"));
        this.activeModal = null;
        this.lastFocused = null;

        this.onOpenClick = this.onOpenClick.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.openers.forEach((btn) => btn.addEventListener("click", this.onOpenClick));
        this.querySelectorAll("[data-tgae-close]").forEach((btn) =>
          btn.addEventListener("click", this.onCloseClick)
        );

        // Nested report viewer, one layer above a citation panel.
        this.viewers = Array.from(this.querySelectorAll("[data-tgae-viewer]"));
        this.activeViewer = null;
        this.onViewClick = this.onViewClick.bind(this);
        this.onViewCloseClick = this.onViewCloseClick.bind(this);
        this.querySelectorAll("[data-tgae-view]").forEach((btn) =>
          btn.addEventListener("click", this.onViewClick)
        );
        this.querySelectorAll("[data-tgae-viewclose]").forEach((btn) =>
          btn.addEventListener("click", this.onViewCloseClick)
        );
      }

      disconnectedCallback() {
        this.close();
        if (this.openers) {
          this.openers.forEach((btn) => btn.removeEventListener("click", this.onOpenClick));
        }
      }

      onOpenClick(event) {
        const index = event.currentTarget.getAttribute("data-tgae-open");
        const modal = this.modals.find(
          (m) => m.getAttribute("data-tgae-modal") === index
        );
        if (!modal) return;
        event.preventDefault();
        this.lastFocused = event.currentTarget;
        this.open(modal);
      }

      onViewClick(event) {
        event.preventDefault();
        const index = event.currentTarget.getAttribute("data-tgae-view");
        const viewer = this.viewers.find(
          (v) => v.getAttribute("data-tgae-viewer") === index
        );
        if (!viewer) return;
        this.viewerOpener = event.currentTarget;
        this.activeViewer = viewer;
        viewer.hidden = false;
        requestAnimationFrame(() => viewer.classList.add("is-open"));
        const close = viewer.querySelector(".tg-adult-evidence__viewer-close");
        if (close) close.focus({ preventScroll: true });
      }

      onViewCloseClick(event) {
        event.preventDefault();
        this.closeViewer();
      }

      closeViewer() {
        const v = this.activeViewer;
        if (!v) return;
        v.classList.remove("is-open");
        v.hidden = true;
        this.activeViewer = null;
        if (this.viewerOpener) {
          this.viewerOpener.focus({ preventScroll: true });
          this.viewerOpener = null;
        }
      }

      onCloseClick(event) {
        event.preventDefault();
        this.close();
      }

      open(modal) {
        if (this.activeModal) this.close();

        this.activeModal = modal;
        modal.hidden = false;
        // Next frame so the transition has a start state to animate from.
        requestAnimationFrame(() => modal.classList.add("is-open"));

        document.body.classList.add("tg-adult-evidence-lock");
        document.addEventListener("keydown", this.onKeydown);

        const focusTarget =
          modal.querySelector(".tg-adult-evidence__modal-close") || modal;
        focusTarget.focus({ preventScroll: true });
      }

      close() {
        this.closeViewer();
        const modal = this.activeModal;
        if (!modal) return;

        modal.classList.remove("is-open");
        modal.hidden = true;
        this.activeModal = null;

        document.body.classList.remove("tg-adult-evidence-lock");
        document.removeEventListener("keydown", this.onKeydown);

        if (this.lastFocused) {
          this.lastFocused.focus({ preventScroll: true });
          this.lastFocused = null;
        }
      }

      onKeydown(event) {
        if (event.key === "Escape") {
          event.preventDefault();
          // One layer at a time: the report viewer closes back to its citation
          // panel rather than dismissing both at once.
          if (this.activeViewer) this.closeViewer();
          else this.close();
          return;
        }

        // Minimal focus trap. Keeps tabbing inside the open panel so the
        // shopper cannot tab into the page behind it while it is covered.
        if (event.key !== "Tab" || !this.activeModal) return;

        const scope = this.activeViewer || this.activeModal;
        const focusables = scope.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
